<template>
  <v-row no-gutters class="pt-5 pr-2">
    <v-col>
      <card-title-row
        style="font-size: 18px !important; gap: 2rem"
        translation-path="sidebar.project"
        :straight-title="currentProject?.name"
        :amount="1"
        :is-plural="true"
        :class="{ 'no-print': isQuickPrint }"
      >
        <template #postTitle>
          <v-subheader
            class="pa-0"
            style="font-style: italic; font-size: 13px; opacity: 0.8; height: auto"
            v-text="isUnsavedChangeTextVisible"
          />
        </template>
        <template #buttons>
          <base-button
            icon="mdi-content-save"
            @click="saveProjectEditing()"
            :tooltip="saveProjectTooltip"
            :disabled="isSaveButtonDisabled || isSaveProjectDisabled"
            :loading="projectEditingLoading"
          />
          <dots-menu
            v-if="isProjectLayersFetched"
            :items="dotsMenuItems"
            @delete="showProjectActionDialog('delete')"
            @markAsDefault="showProjectActionDialog('setDefault')"
            @markAsPredefined="showProjectActionDialog('setPredefined')"
            @unmarkAsPredefined="showProjectActionDialog('unsetPredefined')"
            @changeName="showProjectDialog({ edit: true })"
            @share="isShareDialogVisible = true"
            @copy="showProjectDialog({ copy: true })"
            @createProject="showProjectDialog"
          />
        </template>
      </card-title-row>
      <sidebar-row v-if="temporaryLayers.length">
        <temporary-layers-list :layers="temporaryLayers" />
      </sidebar-row>
      <template v-if="isProjectLayersFetched">
        <card-title-row
          class="pt-6 pb-1 tw:[&_.title-row]:gap-x-1.5"
          :class="{ 'no-print': isQuickPrint }"
          translation-path="sidebar.layers"
          :is-subtitle="true"
          :amount="2"
          :is-plural="true"
        >
          <template v-if="isAllowedToManageProjects" #postTitle>
            <span>
              <base-button
                type="alternative"
                @click="goToLayers"
                icon="mdi-cog"
                :tooltip="$i18n.t('button.goToLayers')"
                color="primary"
              />
            </span>
          </template>
          <template #buttons>
            <base-button
              type="alternative"
              :disabled="isAddLayerDisabled"
              :tooltip="addGroupTooltip"
              icon="mdi-folder-plus-outline"
              @click="showAddGroupDialog"
            />
            <base-button
              :disabled="isAddLayerDisabled"
              :tooltip="addLayerTooltip"
              icon="mdi-plus"
              @click="showAddLayersDialog"
            />
          </template>
        </card-title-row>
        <sidebar-row :has-icon-column="false" slotCols="12" class="pl-3">
          <v-row no-gutters>
            <div v-if="hasGroupWithLayers" style="width: 2%"></div>
            <div :style="`width: ${hasGroupWithLayers ? '98' : '100'}%`">
              <legend-layers-list
                :class="[{ 'pl-1': hasGroupWithLayers ? false : true }, { 'print-legend_layers': isQuickPrint }]"
                key="project"
                @hasUnsavedChanges="hasUnsavedChanges = $event"
                v-model="layers"
                :is-manipulation-disabled="isAddLayerDisabled"
                :groups-names="groupsNames"
              />
            </div>
          </v-row>
        </sidebar-row>
      </template>
      <main-loader v-else class="pt-10" />
    </v-col>

    <!-- Warning with unsaved changes -->
    <main-dialog
      v-if="isWarningDialogLoaded"
      :is-visible.sync="isWarningDialogVisible"
      :title="$i18n.t('dialog.unsavedChanges')"
      :body="$i18n.t('dialog.unsavedProjectChanges')"
    >
      <template #buttons>
        <v-spacer></v-spacer>
        <base-button type="alternative" @click="isWarningDialogVisible = false">
          {{ $i18n.t('dialog.disagree') }}
        </base-button>
        <base-button color="error" @click="onWarningDiscard">
          {{ $i18n.t('dialog.discard') }}
        </base-button>
        <base-button @click="onWarningAgree">
          {{ $i18n.t('dialog.agreeSave') }}
        </base-button>
      </template>
    </main-dialog>

    <!-- Project dialog: create, edit, copy -->
    <project-dialog
      ref="project-dialog"
      :is-visible.sync="isProjectDialogVisible"
      :is-copy.sync="isProjectDialogCopy"
      :as-copy.sync="isProjectDialogAsCopy"
      :is-edit.sync="isProjectDialogEdit"
      @agreed="projectChanged"
      @hasUnsavedChanges="hasUnsavedChanges = $event"
    />

    <!-- Project action dialog: delete, set/unset as predefined/default -->
    <project-action-dialog
      :is-visible.sync="isProjectActionDialogVisible"
      :action.sync="currentProjectAction"
      @agreed="projectChanged"
    />

    <!-- Project share dialog -->
    <project-share-dialog :is-visible.sync="isShareDialogVisible" />

    <!-- Layers adding to project dialog -->
    <layers-adding
      :is-layers-adding-visible.sync="isAddLayersDialogVisible"
      :layers="projectLayersList"
      @addLayers="addLayersHandler"
      :loading="isAddingLayerLoading"
    />

    <!-- Group adding to project dialog -->
    <main-input-dialog
      v-if="isAddGroupDialogVisible"
      :is-visible.sync="isAddGroupDialogVisible"
      :title-translation="$i18n.t('dialog.addGroup')"
      agree-text="dialog.agreeAdd"
      @agree="addGroupHandler"
      :inputs="addGroupInputs"
    />
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DotsMenu from '@/components/DotsMenu';
import LegendLayersList from '@/components/LegendLayersList';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import LayersAdding from '@/components/LayersAdding';
import TemporaryLayersList from '@/components/TemporaryLayersList';

export default {
  name: 'TheLegend',
  components: {
    SidebarRow,
    CardTitleRow,
    DotsMenu,
    LegendLayersList,
    MainLoader,
    TemporaryLayersList,
    MainDialog: () => import('@/components/MainDialog'),
    LayersAdding,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    ProjectDialog: () => import('@/components/ProjectDialog'),
    ProjectActionDialog: () => import('@/components/ProjectActionDialog'),
    ProjectShareDialog: () => import('@/components/ProjectShareDialog'),
  },
  data: () => ({
    groupUniqueId: 0,
    isAddingLayerLoading: false,
    projectEditingLoading: false,
    hasUnsavedChanges: false,
    wasWarningDisplayed: false,
    isChangeNameDialogVisible: false,
    isChangeNameDialogLoaded: false,
    isWarningDialogVisible: false,
    isWarningDialogLoaded: false,
    warningProjectData: undefined,
    isProjectActionDialogVisible: false,
    currentProjectAction: undefined,
    isProjectDialogCopy: false,
    isProjectDialogAsCopy: false,
    isProjectDialogEdit: false,
    isProjectDialogVisible: false,
    isShareDialogVisible: false,
    isAddLayersDialogVisible: false,
    isAddGroupDialogVisible: false,
    isLoading: false,
  }),
  computed: {
    mapRotationModuleEnabled: get('admin/additionalModules@MAP_ROTATION_MODULE.enabled'),
    project: sync('layers/project'),
    currentProject: get('layers/projects@:currentProjectId'),
    currentProjectId: get('layers/project.id'),
    isModifySnappingActive: get('edit/isModifySnappingActive'),
    unchangedProject: get('layers/unchangedProject'),
    temporaryLayers: get('layers/temporaryLayers'),
    projects: sync('layers/projects'),
    layers: sync('layers/project@layers'),
    hasGroupWithLayers() {
      return this.layers?.find(layer => layer.layers?.length);
    },
    isQuickPrint: get('tools/toolStatus@isQuickPrintToolActive'),
    isMapViewChanged: sync('map/isMapViewChanged'),
    isProjectLayersFetched: sync('sidebar/isProjectLayersFetched'),
    users: get('users/users'),
    currentUser: get('users/currentUser'),
    currentUserId: get('users/currentUser@id'),
    isUnsavedChangeTextVisible() {
      return (this.isMapViewChanged || this.hasUnsavedChanges) && (!this.isProjectDefault || !this.isSharedProject)
        ? this.$i18n.t('sidebar.unsavedChanges')
        : '';
    },
    isSaveButtonDisabled() {
      if (this.isProjectDefault || this.isProjectPredefined) {
        return (
          !this.isProjectLayersFetched ||
          (!this.isMapViewChanged && !this.hasUnsavedChanges && !!this.isAllowedToManageProjects)
        );
      } else {
        return (
          !this.isProjectLayersFetched || (!this.isMapViewChanged && !this.hasUnsavedChanges && !this.isSharedProject)
        );
      }
    },
    projectLayersList() {
      return this.$_getFlatGroupsLayers(this.layers || []).layers;
    },
    groups() {
      return this.$_getFlatGroupsLayers(this.layers).groups;
    },
    groupsNames() {
      return this.groups.map(group => group.name);
    },
    addGroupInputs() {
      return [
        {
          name: 'name',
          value: '',
          translationPath: 'dialog.groupName',
          cols: '12',
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v => !this.groupsNames.includes(v) || 'rules.alreadyGroupWithName',
          ],
          dataType: {
            name: 'text',
          },
        },
      ];
    },
    createProjectDisabled() {
      return !this.isProjectLayersFetched || this.isModifySnappingActive;
    },
    createProjectTooltip() {
      return this.isModifySnappingActive
        ? this.$i18n.t('button.toCreateProject', { reason: this.$i18n.t('button.turnOffEditing') })
        : null;
    },
    usersArray() {
      return this.$_objectToArray(this.users);
    },
    flteredUsers() {
      return this.usersArray.filter(user => user.id !== this.currentUserId);
    },
    isAllowedToManageProjects() {
      return this.currentUser.is_superadmin || this.currentUser.is_admin;
    },
    dotsMenuItems() {
      return [
        ...(this.isUserProject || (!this.isSharedProject && this.isAllowedToManageProjects)
          ? [
              {
                name: 'changeName',
              },
            ]
          : []),
        ...(this.isUserProject
          ? [
              {
                name: 'share',
              },
            ]
          : []),
        {
          name: 'copy',
        },
        ...(this.isAllowedToManageProjects && !this.isProjectDefault && !this.isSharedProject
          ? [
              {
                name: 'markAsDefault',
              },
              ...(this.isProjectPredefined
                ? [
                    {
                      name: 'unmarkAsPredefined',
                    },
                  ]
                : [
                    {
                      name: 'markAsPredefined',
                    },
                  ]),
            ]
          : []),
        ...(this.isUserProject || (this.isProjectPredefined && this.isAllowedToManageProjects)
          ? [
              {
                name: 'delete',
              },
            ]
          : []),
        {
          name: 'createProject',
          disabled: this.createProjectDisabled,
        },
      ];
    },
    isDisplayWarning() {
      return this.hasUnsavedChanges && !this.wasWarningDisplayed;
    },
    isProjectDefault() {
      return this.project && this.project.role === 'default';
    },
    isProjectPredefined() {
      return this.project && this.project.role === 'predefined';
    },
    isSharedProject() {
      return !this.isProjectDefault && !this.isProjectPredefined && this.currentUserId !== this.project?.owner;
    },
    isUserProject() {
      return !this.isProjectDefault && !this.isProjectPredefined && this.currentUserId === this.project?.owner;
    },
    isAddLayerDisabled() {
      return (
        this.isSharedProject ||
        ((this.isProjectDefault || this.isProjectPredefined) && !this.isAllowedToManageProjects) ||
        this.isModifySnappingActive
      );
    },
    isSaveProjectDisabled() {
      return (
        this.isSharedProject || ((this.isProjectDefault || this.isProjectPredefined) && !this.isAllowedToManageProjects)
      );
    },
    currentProjectType() {
      return this.project.role ? this.project.role : this.isSharedProject ? 'shared' : 'owner';
    },
    addLayerTooltip() {
      if (this.isModifySnappingActive) {
        return this.$i18n.t('button.toAddLayer', { reason: this.$i18n.t('button.turnOffEditing') });
      }
      if (this.isSharedProject && !this.isProjectDefault && !this.isProjectPredefined) {
        return this.$i18n.t('sidebar.sharedProjectAddDisabled');
      } else if (this.isProjectPredefined && !this.isAllowedToManageProjects) {
        return this.$i18n.t('sidebar.predefinedProjectAddDisabled');
      } else if (this.isProjectDefault && !this.isAllowedToManageProjects) {
        return this.$i18n.t('sidebar.defaultProjectAddDisabled');
      }
      return this.$i18n.t('button.addLayer');
    },
    addGroupTooltip() {
      if (this.isModifySnappingActive) {
        return this.$i18n.t('button.toAddGroup', { reason: this.$i18n.t('button.turnOffEditing') });
      }
      if (this.isSharedProject && !this.isProjectDefault && !this.isProjectPredefined) {
        return this.$i18n.t('sidebar.sharedProjectAddGroupDisabled');
      } else if (this.isProjectPredefined && !this.isAllowedToManageProjects) {
        return this.$i18n.t('sidebar.predefinedProjectAddGroupDisabled');
      } else if (this.isProjectDefault && !this.isAllowedToManageProjects) {
        return this.$i18n.t('sidebar.defaultProjectAddGroupDisabled');
      }
      return this.$i18n.t('button.addGroup');
    },
    saveProjectTooltip() {
      if (this.isSharedProject && !this.isProjectDefault && !this.isProjectPredefined) {
        return this.$i18n.t('sidebar.sharedProjectSave');
      } else if (this.isProjectDefault && !this.isAllowedToManageProjects) {
        return this.$i18n.t('sidebar.defaultProjectSave');
      } else if (this.isProjectPredefined && !this.isAllowedToManageProjects) {
        return this.$i18n.t('sidebar.predefinedProjectSave');
      } else if (!this.hasUnsavedChanges && !this.isMapViewChanged) {
        return this.$i18n.t('sidebar.noChanges');
      }
      return this.$i18n.t('button.saveChanges');
    },
  },
  methods: {
    updateLegendStylesCounts: call('legend/updateLegendStylesCounts'),
    createGtagEvent: call('gtag/createEvent'),
    editProject: call('layers/editProject'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    projectChanged({ e, withRouterParse, force = false } = {}) {
      if (this.isDisplayWarning) this.showWarning({ e, withRouterParse, force });
      else this.switchProject({ e, withRouterParse, force });
    },
    switchProject({ e, withRouterParse, force = false } = {}) {
      if (e === null) return;
      if (e) {
        if (!force && this.$route.query.project === e.id) return;
        this.project = e;
      }
      this.isProjectLayersFetched = false;
      this.hasUnsavedChanges = false;
      this.isMapViewChanged = false;
      this.$root.$emit('removeAllTemporaryLayers');
      this.$root.$emit('fetchProject', { withRouterParse });
    },
    async onWarningAgree() {
      this.isWarningDialogVisible = false;
      this.isWarningDialogLoaded = false;
      this.wasWarningDisplayed = true;
      await this.saveProjectEditing();
      if (this.warningProjectData) this.switchProject(this.warningProjectData);
      if (this.currentProjectAction) {
        this.showProjectActionDialog(this.currentProjectAction);
      }
    },
    async onWarningDiscard() {
      this.isWarningDialogVisible = false;
      this.isWarningDialogLoaded = false;
      this.wasWarningDisplayed = true;
      if (this.warningProjectData) {
        this.switchProject(this.warningProjectData);
      } else {
        await this.discardProjectEditing();
      }
      if (this.currentProjectAction) {
        this.showProjectActionDialog(this.currentProjectAction);
      }
    },
    setCurrentLayer(layer) {
      this.$router.push({
        name: 'layerTable',
        params: {
          lid: layer.id,
        },
      });
    },
    goToLayers() {
      this.$router.push({ name: 'data' });
    },
    async saveProjectEditing() {
      this.projectCopy = JSON.parse(JSON.stringify(this.project));
      let payload = JSON.parse(JSON.stringify(this.project));
      payload.layers = payload.layers.map(layer => {
        if (typeof layer.id === 'string' && layer.id?.includes('ugi-')) {
          delete layer.id;
        }
        return { ...layer };
      });
      this.createGtagEvent({
        action: 'save_project_editing',
        category: 'map_legend',
        params: {
          name: this.project.name,
          id: this.project.id,
        },
      });

      try {
        this.projectEditingLoading = true;
        await this.editProject({ body: payload, id: this.project.id });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successProjectEdit'),
        });
        this.hasUnsavedChanges = false;
        this.isMapViewChanged = false;
        this.wasWarningDisplayed = false;
        this.projectEditingLoading = false;
      } catch (e) {
        console.log(e);
        this.projectEditingLoading = false;
      }
    },
    discardProjectEditing() {
      this.$store.set('layers/SET_PROJECT!', { ...this.unchangedProject });
      this.$store.set('layers/DISCARD_PROJECT!', { ...this.unchangedProject });
      this.$root.$emit('discardProjectLayers');
      this.isMapViewChanged = false;
      this.wasWarningDisplayed = false;
      this.$nextTick(() => {
        this.hasUnsavedChanges = false;
      });
    },
    showProjectActionDialog(action) {
      this.currentProjectAction = action;
      if (['setDefault', 'setPredefined', 'unsetPredefined'].includes(action) && this.isDisplayWarning) {
        this.showWarning();
        return;
      }
      this.isProjectActionDialogVisible = true;
      if (action === 'delete')
        this.createGtagEvent({
          action: 'open_project_delete_dialog',
          category: 'map_legend',
        });
    },
    showWarning(data = {}) {
      this.isWarningDialogVisible = true;
      this.isWarningDialogLoaded = true;
      this.warningProjectData = Object.keys(data).length ? data : undefined;
    },
    showProjectDialog({ copy = false, asCopy = false, edit = false } = {}) {
      this.isProjectDialogCopy = copy;
      this.isProjectDialogAsCopy = asCopy;
      this.isProjectDialogEdit = edit;
      if (edit || copy) this.$refs['project-dialog'].projectName = this.project?.name || '';
      this.isProjectDialogVisible = true;
    },
    showAddLayersDialog() {
      this.isAddLayersDialogVisible = true;

      this.createGtagEvent({
        action: 'open_add_layer_dialog',
        category: 'map_legend',
      });
    },
    showAddGroupDialog() {
      this.isAddGroupDialogVisible = true;
    },
    addGroupHandler() {
      const value = JSON.parse(JSON.stringify(this.addGroupInputs.find(input => input.name === 'name').value));
      this.$store.set('layers/ADD_PROJECT_GROUP!', { value, id: `ugi-${this.groupUniqueId}` });
      this.groupUniqueId++;
      this.isAddGroupDialogVisible = false;
      this.addGroupInputs.find(input => input.name === 'name').value = '';
      this.hasUnsavedChanges = true;
      this.$forceUpdate();
    },
    async addLayersHandler(layersChecked) {
      this.isAddingLayerLoading = true;
      const mapLayer = layer => {
        const layerCopy = { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer };
        delete layerCopy.key;
        delete layerCopy.style_qgis;
        delete layerCopy.style_qgis_file_name;
        if (layer.type === 'features_layer') {
          layerCopy.labels_visible = layerCopy.labels_visible || false;
          layerCopy.style = layerCopy.style_web;
          layerCopy.cache = layerCopy.schema === 'cache' ? true : false;
          delete layerCopy.style_web;
          delete layerCopy.schema;
        } else if (layer.type === 'service_layer') {
          layerCopy.style = layerCopy.style_web;
          delete layerCopy.style_web;
        }
        return layerCopy;
      };
      const mapGroup = group => {
        return {
          name: this.$_getNewUniqueName(this.groupsNames, group.name),
          type: 'group',
          layers: group.children.map(mapLayer),
        };
      };
      const layersToAdd = layersChecked.map(element => {
        if (element.children) return mapGroup(element);
        else return mapLayer(element);
      });
      this.$store.set('layers/ADD_PROJECT_LAYERS!', layersToAdd);
      this.$root.$emit('pushProjectLayers', this.$_flattenStructure(layersToAdd, 'layers'));
      this.isAddingLayerLoading = false;
      this.hasUnsavedChanges = true;
      this.layersChecked = [];
      this.isAddLayersDialogVisible = false;

      layersChecked.forEach(layer => {
        this.createGtagEvent({
          action: 'add_layer',
          category: 'map_legend',
          params: {
            type: layer.type,
            name: layer.name,
            id: layer.id,
          },
        });
      });
    },
  },
  mounted() {
    this.$root.$off('addLayersHandler');
    this.$root.$on('addLayersHandler', this.addLayersHandler);
    this.$root.$off('updateLegendStylesCounts');
    this.$root.$on('updateLegendStylesCounts', this.updateLegendStylesCounts);
    this.$root.$off('hasUnsavedChanges');
    this.$root.$on('hasUnsavedChanges', value => {
      this.hasUnsavedChanges = value;
    });
    this.$root.$off('projectChanged');
    this.$root.$on('projectChanged', this.projectChanged);
    this.updateLegendStylesCounts();
  },
};
</script>

<style lang="scss" scoped>
.border {
  @include border(bottom);
}
.v-menu__content {
  max-width: 30% !important;
}
.add-project-wrapper {
  position: sticky !important;
  bottom: 0;
  background-color: white;
  z-index: 1;
}
::v-deep .new__project__name .v-input__slot {
  margin-bottom: 0;
}
::v-deep .new__project__name .v-messages__message {
  color: rgba(0, 0, 0, 0.59);
  font-size: 10px;
  font-style: italic;
}
::v-deep .new__project__name .v-text-field__details {
  padding: 0;
}
::v-deep .v-messages__message {
  font-size: 10px;
  font-style: italic;
}
::v-deep .v-input__slot {
  margin-bottom: 3px;
}
::v-deep .main-dialog {
  .v-btn.down {
    margin-left: 14px !important;
    min-width: 90px;
    text-transform: initial;
    &.cancel {
      border-color: rgba(17, 10, 10, 0.2);
      &:hover {
        box-shadow:
          0 3px 3px -2px rgba(0, 0, 0, 0.2),
          0 3px 4px 0 rgba(0, 0, 0, 0.14),
          0 1px 8px 0 rgba(0, 0, 0, 0.12) !important;
      }
    }
  }
}
</style>
